<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100 d-flex justify-content-between align-items-center">
          <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">{{ $t("edit_attendance") }}</h6>
        </div>
      </div>

      <LoadingSpinner v-if="isLoading" :message="$t('loading_attendance')" />

      <div v-else class="card-body px-0 pb-2 pe-3">
        <div class="row justify-content-center">
          <div class="col-md-8 col-lg-6">
            <div class="p-4">
              <div class="mb-4">
                <label class="form-label">{{ $t("teacher_name") }}</label>
                <input type="text" class="form-control borderless-input" :value="teacherName" disabled>
              </div>

              <div class="mb-4">
                <label class="form-label">{{ $t("date") }}</label>
                <input type="text" class="form-control borderless-input" :value="formattedDate" disabled>
              </div>

              <div class="mb-4">
                <label class="form-label">{{ $t("indexstatus") }}</label>
                <select class="form-select borderless-input" v-model="status" :disabled="isSaving">
                  <option value="present">{{ $t("present") }}</option>
                  <option value="absent">{{ $t("absent") }}</option>
                  <option value="late">{{ $t("late") }}</option>
                  <option value="excused">{{ $t("excused") }}</option>
                  <option value="on_leave">{{ $t("on_leave") }}</option>
                </select>
              </div>

              <div class="mb-4">
                <label class="form-label">{{ $t("notes") }}</label>
                <textarea class="form-control borderless-input" v-model="notes" rows="3" :disabled="isSaving"></textarea>
              </div>

              <div class="mt-4 d-flex gap-2">
                <button @click="saveChanges" class="btn btn-primary btn-sm flex-grow-1" :disabled="isSaving || !hasChanges">
                  <span v-if="isSaving">
                    <i class="fas fa-spinner fa-spin me-2"></i> {{ $t("saving") }}
                  </span>
                  <span v-else>
                    <i class="fas fa-edit me-2"></i> {{ $t("save_changes") }}
                  </span>
                </button>
                <button @click="goBack" class="btn btn-outline-secondary btn-sm flex-grow-1" :disabled="isSaving">
                  <i class="fas fa-times me-2"></i> {{ $t("cancel") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import api from '@/api';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const toast = useToast();

const attendance = ref({
  id: null,
  teacher: { name: '' },
  date: '',
  status: '',
  notes: ''
});

const status = ref('');
const notes = ref('');
const isSaving = ref(false);
const isLoading = ref(true);

const teacherName = computed(() => {
  return attendance.value?.teacher?.name || attendance.value?.teacher?.username || t("unknown_teacher");
});

const formattedDate = computed(() => {
  if (!attendance.value?.date) return t("not_specified");

  try {
    const dateObj = new Date(attendance.value.date);
    if (isNaN(dateObj.getTime())) return t("invalid_date");
    
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return dateObj.toLocaleDateString('ar-LY', options);
  } catch (e) {
    console.error("Date formatting error:", e);
    return t("invalid_date");
  }
});

const hasChanges = computed(() => {
  return status.value !== attendance.value.status || notes.value !== (attendance.value.notes || '');
});

const fetchAttendance = async () => {
  try {
    const response = await api.get(`/teacher-attendances/${route.params.id}`, {
      params: { include: 'teacher' }
    });
    attendance.value = response.data.data;
    status.value = attendance.value.status;
    notes.value = attendance.value.notes || '';
    console.log('Fetched attendance:', attendance.value);
  } catch (error) {
    console.error("Error fetching attendance:", error);
    toast.error(t("load_fail"));
    router.push('/teacher-attendances');
  } finally {
    isLoading.value = false;
  }
};

const saveChanges = async () => {
  if (!hasChanges.value) {
    toast.info(t("no_changes"));
    return;
  }

  isSaving.value = true;
  try {
    const response = await api.put(`/teacher-attendances/${attendance.value.id}`, {
      status: status.value,
      notes: notes.value
    });

    if (response.data.success) {
      toast.success(response.data.message || t("update_success"));
      attendance.value = response.data.data;
      router.push('/teacher-attendances');
    } else {
      throw new Error(response.data.message || t("update_fail"));
    }

  } catch (error) {
    const errorMsg = error.response?.data?.message || 
                     error.response?.data?.error || 
                     error.message ||
                     t("update_fail");
    toast.error(errorMsg);
    console.error("Error details:", error.response?.data || error);
  } finally {
    isSaving.value = false;
  }
};

const goBack = () => {
  router.push('/teacher-attendances');
};

onMounted(() => {
  fetchAttendance();
});
</script>
